<script>
  import { onMount } from 'svelte';
  import { fly, fade, scale } from 'svelte/transition';
  import { createEventDispatcher } from 'svelte';
  import GiftIcon from './icons/GiftIcon.svelte';
  import PrizeStarIcon from './icons/PrizeStarIcon.svelte';
  import RouletteIcon from './icons/RouletteIcon.svelte';
  import AssignmentIcon from './icons/AssignmentIcon.svelte';
  import CheckIcon from './icons/CheckIcon.svelte';
  import LinkIcon from './icons/LinkIcon.svelte';

  export let authData; // Получаем от родительского компонента

  const dispatch = createEventDispatcher();

  let loading = true;
  let error = null;
  let tasks = []; // { id: number, title: string, description: string, rewardSpins: number, status: 'available' | 'pending' | 'completed' | 'claimed', link?: string }
  let checkingTaskId = null; // ID задачи, которая проверяется
  let claimingTaskId = null; // ID задачи, которая забирается

  async function fetchTasks() {
    loading = true;
    error = null;
    try {
      const response = await fetch('/api/v1/tasks', { // ЗАПРОС К БЭКЕНДУ
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-Auth': authData
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      tasks = await response.json(); // Ожидаем массив задач от бэка
    } catch (err) {
      console.error("Failed to load tasks data:", err);
      error = "Не удалось загрузить задания.";
      dispatch('error', { message: error });
    } finally {
      loading = false;
    }
  }

  onMount(() => {
      if (authData) {
        fetchTasks();
    } else {
        error = "Не удалось получить данные аутентификации Telegram.";
        loading = false;
        console.warn("Auth data is missing, cannot fetch tasks.");
    }
  });

  // Функция для обработки клика по кнопке задачи
  async function handleTaskAction(task) {
    if (checkingTaskId === task.id || claimingTaskId === task.id) return;

    switch (task.status) {
      case 'available':
        if (task.link) {
          window.open(task.link, '_blank');
        } else {
           await checkTaskStatus(task);
        }
        break;

      case 'pending':
        await checkTaskStatus(task);
        break;

      case 'completed':
         await claimTaskReward(task);
        break;

      case 'claimed':
        // Ничего не делаем
        break;
    }
  }

  // Функция проверки статуса задачи
  async function checkTaskStatus(task) {
      checkingTaskId = task.id;
      try {
          const response = await fetch(`/api/v1/tasks/${task.id}/check`, { // ЗАПРОС К БЭКЕНДУ
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'X-Telegram-Auth': authData
              }
          });
          if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
          }
          const result = await response.json(); // { status: 'pending' | 'completed' }
          // Обновляем статус конкретной задачи
          const taskIndex = tasks.findIndex(t => t.id === task.id);
          if (taskIndex !== -1) {
              tasks[taskIndex].status = result.status;
              tasks = tasks; // Триггер обновления Svelte
          }
          // Можно показать уведомление типа "Проверка..." -> "Выполнено!" или "Пока не выполнено"
      } catch(err) {
           console.error("Ошибка проверки задания:", err);
           dispatch('notify', { message: `Ошибка проверки: ${err.message}`, type: 'error' });
      } finally {
           checkingTaskId = null;
      }
  }

  
   // Функция получения награды за задачу
  async function claimTaskReward(task) {
      claimingTaskId = task.id;
      try {
           const response = await fetch(`/api/v1/tasks/${task.id}/claim`, { // ЗАПРОС К БЭКЕНДУ
               method: 'POST',
               headers: {
                   'Content-Type': 'application/json',
                   'X-Telegram-Auth': authData
               }
           });
           if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
           }
           const result = await response.json(); // { success: true, status: 'claimed', addedSpins: number }
           // Обновляем статус и диспатчим спины
           const taskIndex = tasks.findIndex(t => t.id === task.id);
           if (taskIndex !== -1) {
               tasks[taskIndex].status = result.status;
               tasks = tasks; // Триггер обновления Svelte
               if (result.addedSpins > 0) {
                   dispatch('addSpins', result.addedSpins);
                   dispatch('notify', { message: `+${result.addedSpins} прокрутов!`, type: 'success' });
               }
           }
       } catch(err) {
            console.error("Ошибка получения награды:", err);
            dispatch('notify', { message: `Ошибка: ${err.message}`, type: 'error' });
       } finally {
            claimingTaskId = null;
       }
  }

  // Функция для получения пропсов кнопки (остается на фронте)
  function getButtonProps(status) {
    switch (status) {
      case 'available': return { text: 'Перейти', icon: LinkIcon, class: 'go' };
      case 'pending': return { text: 'Проверить', icon: CheckIcon, class: 'check', loading: checkingTaskId }; // Добавим loading
      case 'completed': return { text: 'Забрать', icon: GiftIcon, class: 'claim', loading: claimingTaskId }; // Добавим loading
      case 'claimed': return { text: 'Получено', icon: CheckIcon, class: 'claimed', disabled: true };
      default: return { text: '...', disabled: true };
    }
  }
  
  // Функция для открытия рекламной ссылки
  function openPromotionLink() {
    window.open('https://justonesec.ru/stream/1kstarrs', '_blank');
  }
</script>


<div class="tab-content-container tasks-tab">
   {#if loading}
    <div class="loading-placeholder">
      <div class="spinner large"></div>
      <p>Загружаем список заданий...</p>
    </div>
  {:else if error}
    <div class="error-message" transition:fade>
      <p>{error}</p>
      <button class="retry-button small" on:click={() => window.location.reload()}>Попробовать снова</button>
    </div>
  {:else}
    <!-- Новый рекламный блок -->
    <div class="promo-card" in:fly={{ y: 15, duration: 350 }}>
      <div class="promo-stars">
        <PrizeStarIcon size="42" />
      </div>
      <div class="promo-content">
        <h3 class="promo-title">Как получить Telegram Stars:</h3>
        <ol class="promo-steps">
          <li>Переходим по ссылке <span class="highlight" on:click={openPromotionLink}>ТЫК</span> на сайт</li>
          <li>Оплачиваем 1₽ за доступ к аналитике на 5 дней</li>
          <li>Получи 1000₽ от партнера, а также другие подарки и зарабатывай на Telegram Stars</li>
        </ol>
        <button class="promo-button" on:click={openPromotionLink}>
          <span class="button-text">ПОЛУЧИТЬ 1000₽</span>
          <span class="button-arrow">→</span>
        </button>  
      </div>
    </div>
    
    {#if tasks.length > 0}
      <div class="tasks-header" transition:fade>
         <AssignmentIcon size="30"/>
         <h2>Доступные задания</h2>
         <p>Выполняйте задания и получайте больше прокрутов!</p>
      </div>
          <ul class="tasks-list">
        {#each tasks as task (task.id)}
          {@const btnProps = getButtonProps(task.status)}
          {@const isChecking = checkingTaskId === task.id}
          {@const isClaiming = claimingTaskId === task.id}
          <li class="task-item card status-{task.status}" in:fly={{ y: 15, duration: 350, delay: task.id * 60 }}>
            <div class="task-info">
              <h3 class="task-title">{task.title}</h3>
              <p class="task-description">{task.description}</p>
            </div>
            <div class="task-action">
              <span class="task-reward">+{task.rewardSpins} <RouletteIcon /></span>
              <button
                class="task-button {btnProps.class}"
                disabled={btnProps.disabled || isChecking || isClaiming}
                on:click={() => handleTaskAction(task)}
              >
                {#if isChecking || isClaiming}
                   <div class="spinner tiny"></div>
                   <span>{isChecking ? 'Проверка...' : 'Получение...'}</span>
                {:else if btnProps.icon}
                  <svelte:component this={btnProps.icon} />
                  <span>{btnProps.text}</span>
                {:else}
                   <span>{btnProps.text}</span>
                {/if}
              </button>
            </div>
          </li>
        {/each}
      </ul>
    {:else}
       <div class="placeholder-content" transition:fade>
          <AssignmentIcon size="60" />
          <h2>Пока нет доступных заданий</h2>
          <p>Загляните позже!</p>
        </div>
    {/if}
  {/if}
</div>


<style>
  /* Подключаем общие стили */
  @import './commonTabStyles.css';
  /* Импортируем иконку звезды */
  @import './icons/PrizeStarIcon.svelte';

  .tasks-tab {
    display: flex;
    flex-direction: column;
    /* если родитель имеет высоту, либо задаём здесь явную, например: */
    height: 100%; /* или height: 100vh; */
    overflow: hidden; /* чтобы сам таб не прокручивался, а список внутри */
  }

  /* Стили для рекламного блока */
  .promo-card {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    padding: 16px;
    background: linear-gradient(135deg, #204060 0%, #2a5783 100%);
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    position: relative;
    overflow: hidden;
  }
  
  .promo-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at top right, rgba(255, 223, 128, 0.1), transparent 60%);
    pointer-events: none;
  }
  
  .promo-stars {
    margin-right: 15px;
    flex-shrink: 0;
    position: relative;
  }
  
  .promo-stars :global(svg) {
    fill: #FFD700;
    filter: drop-shadow(0 0 8px rgba(255, 215, 0, 0.6));
    animation: pulse 2s infinite ease-in-out;
  }
  
  @keyframes pulse {
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.8; }
    100% { transform: scale(1); opacity: 1; }
  }
  
  .promo-content {
    flex: 1;
  }
  
  .promo-title {
    margin: 0 0 10px;
    font-size: 1.2rem;
    font-weight: 700;
    color: #FFFFFF;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
  
  .promo-steps {
    margin: 0 0 15px;
    padding-left: 20px;
    color: #E1E9F0;
    font-size: 0.9rem;
    line-height: 1.4;
  }
  
  .promo-steps li {
    margin-bottom: 5px;
  }
  
  .highlight {
    font-weight: 700;
    color: #FFD700;
    text-decoration: underline;
    cursor: pointer;
  }
  
  .promo-button {
    background: linear-gradient(45deg, #FFB347, #FFCC33);
    color: #1A2C3D;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-size: 0.95rem;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    text-transform: uppercase;
  }
  
  .promo-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
    filter: brightness(1.05);
  }
  
  .button-text {
    margin-right: 8px;
  }
  
  .button-arrow {
    font-size: 1.2em;
    font-weight: bold;
    transition: transform 0.2s ease;
  }
  
  .promo-button:hover .button-arrow {
    transform: translateX(3px);
  }

  .tasks-header {
    text-align: center;
    margin-bottom: 10px;
    width: 100%;
    max-width: 500px;
  }
  .tasks-header :global(svg) { fill: #6EBFFF; opacity: 0.7; margin-bottom: 5px; }
  .tasks-header h2 { margin: 5px 0; font-size: 1.4rem; color: #E1E9F0; }
  .tasks-header p { color: #A6C5E1; font-size: 0.95rem; margin: 0; }

  .tasks-list {
    flex: 1;             
    overflow-y: auto;
    padding-right: 8px;   
  }

  .task-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 15px;
    padding: 12px 15px; /* Компактнее */
    transition: background-color 0.3s ease, border-color 0.3s ease, opacity 0.3s ease;
  }

  .task-info { flex-grow: 1; text-align: left;}
  .task-title {
    margin: 0 0 4px;
    font-size: 1.05rem;
    font-weight: 600;
    color: #D0DCE8;
  }
  .task-description {
    margin: 0;
    font-size: 0.85rem;
    color: #9DB2BF;
    line-height: 1.4;
  }

  .task-action {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    flex-shrink: 0;
  }

 .task-reward {
    font-size: 1rem;
    font-weight: 700;
    color: #87C3FF;
    display: inline-flex;
    align-items: center;
    gap: 3px; 
  }

  .task-reward :global(svg.roulette-icon) {
      width: 1.2em;
      height: 1.2em;
      stroke: currentColor; 
      stroke-width: 1.5; 
      vertical-align: -0.2em;
   }

  .task-button {
    padding: 7px 14px;
    font-size: 0.9rem;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-width: 100px; /* Минимальная ширина кнопки */
    transition: all 0.25s ease;
  }
  .task-button :global(svg) { width: 16px; height: 16px; }
  .task-button span { line-height: 1; } /* Убираем лишний отступ у span */
   .spinner.tiny { width: 14px; height: 14px; border-width: 2px; }

  /* Стили кнопок по статусам */
  .task-button.go { background: linear-gradient(45deg, #42A5F5, #64B5F6); color: white; }
  .task-button.go:hover:not(:disabled) { filter: brightness(1.1); transform: scale(1.03); }

  .task-button.check { background-color: rgba(255, 183, 77, 0.2); color: #FFB74D; border: 1px solid rgba(255, 183, 77, 0.4); }
  .task-button.check:hover:not(:disabled) { background-color: rgba(255, 183, 77, 0.3); }

  .task-button.claim { background: linear-gradient(45deg, #66BB6A, #81C784); color: white; }
  .task-button.claim:hover:not(:disabled) { filter: brightness(1.1); transform: scale(1.03); }

  .task-button.claimed { background-color: rgba(110, 191, 255, 0.1); color: #78909C; border: 1px solid rgba(110, 191, 255, 0.1); cursor: default; }
  .task-button.claimed :global(svg) {  } /* Зеленая галочка */

  .task-button:disabled:not(.claimed) { opacity: 0.6; cursor: not-allowed; }

  /* Стили карточек по статусам */
  .task-item.status-claimed {
     opacity: 0.6;
     background-color: rgba(20, 30, 42, 0.5);
  }
   .task-item.status-completed {
      border-left: 4px solid #66BB6A; /* Зеленая полоса слева */
   }
    .task-item.status-pending {
      border-left: 4px solid #FFB74D; /* Оранжевая полоса слева */
   }

  /* Медиа-запрос для мобильных устройств */
  @media (max-width: 480px) {
    .promo-card {
      flex-direction: column;
      text-align: center;
      padding: 12px;
    }
    
    .promo-stars {
      margin-right: 0;
      margin-bottom: 10px;
    }
    
    .promo-steps {
      text-align: left;
    }
    
    .promo-button {
      width: 100%;
      margin-top: 10px;
    }
  }
</style>